import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Toast from '@/components/base/Toast';
import { useToast } from '@/hooks/useToast';
import { BrowserProvider, formatEther } from 'ethers';

const ETH_TO_USD = 2176.43;
const ETH_RATE = 0.859039;
const BASE_APR = 58; // 연간 APR 58%

// 기간별 단리 계산 (58% APR 기준)
const DURATIONS = [
  { days: 30, label: '30일', apr: Number(((BASE_APR * 30) / 365).toFixed(2)) },
  { days: 60, label: '60일', apr: Number(((BASE_APR * 60) / 365).toFixed(2)) },
  { days: 90, label: '90일', apr: Number(((BASE_APR * 90) / 365).toFixed(2)) },
  { days: 180, label: '180일', apr: Number(((BASE_APR * 180) / 365).toFixed(2)) },
  { days: 365, label: '365일', apr: BASE_APR },
];

type Tab = 'stake' | 'unstake';

export default function StakePage() {
  const [tab, setTab] = useState<Tab>('stake');
  const [ethAmount, setEthAmount] = useState('1');
  const [durationDays, setDurationDays] = useState(30);
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [ethBalance, setEthBalance] = useState('0');
  const [chainId, setChainId] = useState<number | null>(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [user, setUser] = useState<{ id?: string; email?: string } | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const { message: toastMessage, type: toastType, show: showToast, hide: hideToast } = useToast();
  const [progressWidth, setProgressWidth] = useState(0);
  const [loading, setLoading] = useState(false);
  const [connectingWallet, setConnectingWallet] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const navigate = useNavigate();

  // 페이지 마운트 시 애니메이션 트리거
  useEffect(() => {
    setAnimateIn(true);
  }, []);

  // 프로그레스 바 천천히 채워지는 애니메이션
  useEffect(() => {
    setProgressWidth(0);
    const timer = setTimeout(() => setProgressWidth(100), 80);
    return () => clearTimeout(timer);
  }, [durationDays, ethAmount]);

  // 로그인 체크
  useEffect(() => {
    let mounted = true;
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;
      if (!session) {
        navigate('/auth?redirect=/stake');
        return;
      }
      setUser(session.user);
      setAuthLoading(false);
    };
    checkAuth();
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        navigate('/auth?redirect=/stake');
      } else {
        setUser(session.user);
        setAuthLoading(false);
      }
    });
    return () => {
      mounted = false;
      listener?.subscription.unsubscribe();
    };
  }, [navigate]);

  // 지갑 연결 상태 자동 확인
  useEffect(() => {
    const checkWallet = async () => {
      if (!window.ethereum) return;
      try {
        const provider = new BrowserProvider(window.ethereum);
        const accounts = await provider.send('eth_accounts', []);
        if (accounts.length > 0) {
          const signer = await provider.getSigner();
          const address = await signer.getAddress();
          const network = await provider.getNetwork();
          const balance = await provider.getBalance(address);
          setWalletAddress(address);
          setWalletConnected(true);
          setEthBalance(formatEther(balance));
          setChainId(Number(network.chainId));
        }
      } catch { /* noop */ }
    };
    checkWallet();
  }, []);

  // 메타마스크 이벤트 리스너
  useEffect(() => {
    if (!window.ethereum) return;
    const handleAccountsChanged = (accounts: string[]) => {
      if (accounts.length === 0) {
        disconnectWallet();
      } else {
        setWalletAddress(accounts[0]);
      }
    };
    const handleChainChanged = () => {
      window.location.reload();
    };
    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);
    return () => {
      window.ethereum?.removeListener?.('accountsChanged', handleAccountsChanged);
      window.ethereum?.removeListener?.('chainChanged', handleChainChanged);
    };
  }, []);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#070810]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-slate-700 border-t-slate-300 animate-spin" />
          <span className="text-sm text-slate-600">로그인 상태 확인 중...</span>
        </div>
      </div>
    );
  }

  const selectedDuration = DURATIONS.find((d) => d.days === durationDays) || DURATIONS[0];
  const currentAPR = selectedDuration.apr;
  const ethVal = parseFloat(ethAmount) || 0;
  const ecEthAmount = (ethVal * ETH_RATE).toFixed(6);
  const usdValue = (ethVal * ETH_TO_USD).toFixed(2);
  const ecEthUsd = (ethVal * ETH_RATE * ETH_TO_USD).toFixed(2);
  const txCost = '0.00050 ETH (≈ $1.09 USD)';

  // 수익률 실시간 계산 (연 58% APR 기준)
  const dailyRate = BASE_APR / 365; // 하루 수익률 (%)
  const monthlyRate = dailyRate * 30; // 한달(30일) 수익률 (%)
  const periodRate = (BASE_APR * durationDays) / 365; // 선택 기간 수익률 (%)

  // 예상 수익 계산
  const dailyReward = ethVal * (dailyRate / 100);
  const monthlyReward = ethVal * (monthlyRate / 100);
  const periodReward = ethVal * (periodRate / 100);

  const dailyRewardUsd = (dailyReward * ETH_TO_USD).toFixed(2);
  const monthlyRewardUsd = (monthlyReward * ETH_TO_USD).toFixed(2);
  const periodRewardUsd = (periodReward * ETH_TO_USD).toFixed(2);

  // 날짜 계산
  const today = new Date();
  const startDateStr = today.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
  const endDateObj = new Date(today);
  endDateObj.setDate(today.getDate() + durationDays);
  const endDateStr = endDateObj.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });

  const handleMax = () => setEthAmount(parseFloat(ethBalance).toFixed(6));
  const handleConnectWallet = () => {
    if (!walletConnected) {
      showToast('스테이킹/출금을 위해 먼저 지갑을 연결해주세요.', 'warning');
      setShowConnectModal(true);
    }
  };

  const depositAddress = '0x271Fe3692A7CE3DbD0e292aA2f68849e2EA4aaA5';

  const copyDepositAddress = () => {
    navigator.clipboard.writeText(depositAddress).then(() => {
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    });
  };

  const disconnectWallet = () => {
    setWalletConnected(false);
    setWalletAddress(null);
    setEthBalance('0');
    setChainId(null);
  };

  const connectMetaMask = async () => {
    if (!window.ethereum) {
      showToast('MetaMask가 설치되지 않았습니다. 설치 후 다시 시도해주세요.', 'error');
      setShowConnectModal(false);
      return;
    }
    setConnectingWallet(true);
    try {
      const provider = new BrowserProvider(window.ethereum);
      await provider.send('eth_requestAccounts', []);
      const signer = await provider.getSigner();
      const address = await signer.getAddress();
      const network = await provider.getNetwork();
      const balance = await provider.getBalance(address);
      setWalletAddress(address);
      setWalletConnected(true);
      setEthBalance(formatEther(balance));
      setChainId(Number(network.chainId));
      showToast('지갑이 성공적으로 연결되었습니다!', 'success');
      setShowConnectModal(false);
    } catch (err: any) {
      console.error(err);
      const code = err?.code;
      const isUserRejection =
        code === 'ACTION_REJECTED' ||
        code === 4001 ||
        err?.message?.includes('User rejected') ||
        err?.message?.includes('user denied') ||
        err?.message?.includes('rejectAllApprovals');

      if (isUserRejection) {
        showToast('지갑 연결이 취소되었습니다. 다시 시도하려면 지갑 연결 버튼을 눌러주세요.', 'info');
      } else {
        showToast('지갑 연결에 실패했습니다. 잠시 후 다시 시도해주세요.', 'error');
      }
    } finally {
      setConnectingWallet(false);
    }
  };

  const handleStake = async () => {
    if (!user) {
      navigate('/auth?redirect=/stake');
      return;
    }
    if (ethVal <= 0) {
      showToast('스테이킹 수량을 입력해주세요.', 'warning');
      return;
    }
    setLoading(true);
    const { error } = await supabase.from('staking_records').insert({
      user_id: user?.id,
      eth_amount: ethVal,
      eceth_amount: parseFloat(ecEthAmount),
      type: 'stake',
      status: 'pending',
      duration_days: durationDays,
    });
    setLoading(false);
    if (error) {
      showToast('스테이킹 신청 저장에 실패했습니다.', 'error');
    } else {
      showToast('스테이킹 신청이 접수되었습니다! 관리자 승인 후 이자가 시작됩니다.', 'success');
      setEthAmount('1');
      setTimeout(() => navigate('/profile'), 1500);
    }
  };

  const handleUnstake = async () => {
    if (!user) {
      navigate('/auth?redirect=/stake');
      return;
    }
    if (ethVal <= 0) {
      showToast('출금 수량을 입력해주세요.', 'warning');
      return;
    }
    setLoading(true);
    const { error } = await supabase.from('staking_records').insert({
      user_id: user?.id,
      eth_amount: ethVal,
      eceth_amount: parseFloat(ecEthAmount),
      type: 'unstake',
      status: 'pending',
    });
    setLoading(false);
    if (error) {
      showToast('출금 신청 저장에 실패했습니다.', 'error');
    } else {
      showToast('출금 신청이 접수되었습니다! 내역에서 확인하세요.', 'success');
      setEthAmount('1');
      setTimeout(() => navigate('/profile'), 1200);
    }
  };

  return (
    <div className="min-h-screen bg-[#070810] text-slate-200 relative overflow-hidden">
      {/* Ambient background gradients */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.25) 0%, transparent 70%)' }} />
        <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.2) 0%, transparent 70%)' }} />
      </div>

      {/* Toast */}
      <Toast message={toastMessage} type={toastType} onClose={hideToast} />

      {/* Header */}
      <header className="sticky top-0 z-40 flex items-center justify-between px-6 sm:px-10 h-16 border-b border-slate-800/60 bg-[#070810]/80 backdrop-blur-xl">
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          <img
            src="https://storage.readdy-site.link/project_files/bf05668a-4394-4a75-8787-649afaf5b969/2926a449-3ef0-42b6-8c91-63e7ae74eb0e_ecash-logo.png?v=76080c504c8cb6dea86ac894ec394a5c"
            alt="eCash-Pool"
            className="h-5 w-auto opacity-90"
          />
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-300 transition-colors">
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
            홈으로
          </Link>
          <button
            onClick={() => navigate('/profile?tab=info')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-slate-800/60 border border-slate-700/60 text-slate-300 transition-colors hover:bg-slate-700/60 hover:text-white cursor-pointer whitespace-nowrap"
          >
            <i className="ri-user-line w-4 h-4 flex items-center justify-center" />
            내 정보
          </button>
          <button
            onClick={() => navigate('/profile?tab=history')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-slate-800/60 border border-slate-700/60 text-slate-300 transition-colors hover:bg-slate-700/60 hover:text-white cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-list-3-line w-4 h-4 flex items-center justify-center" />
            내역 보기
          </button>
        </div>
      </header>

      {/* Main */}
      <main className="relative z-10 min-h-[calc(100vh-64px)] flex flex-col items-center px-4 py-16">
        <div
          className={`w-full max-w-lg transition-all duration-700 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          {/* Protocol stats */}
          <div className="flex justify-center gap-8 mb-10">
            <div className="text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">총 스테이킹</p>
              <p className="text-lg font-semibold text-slate-100">614,464 ETH</p>
              <p className="text-xs text-slate-600 mt-0.5">19,202 검증자</p>
            </div>
            <div className="w-px bg-slate-800 self-stretch my-1" />
            <div className="text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">수수료</p>
              <p className="text-lg font-semibold text-slate-100">5%</p>
              <p className="text-xs text-slate-600 mt-0.5">경쟁력 있는 수수료</p>
            </div>
          </div>

          {/* Main Card */}
          <div className="rounded-2xl bg-[#0f1117] border border-slate-800/80 overflow-hidden">
            {/* Tabs */}
            <div className="flex bg-[#0a0c12]">
              {(['stake', 'unstake'] as Tab[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className="flex-1 py-4 text-sm font-semibold transition-all duration-200 cursor-pointer relative text-slate-500 hover:text-slate-300"
                  style={{
                    color: tab === t ? '#e2e8f0' : undefined,
                    background: tab === t ? '#0f1117' : 'transparent',
                  }}
                >
                  {t === 'stake' ? 'ETH 스테이킹' : 'ETH 출금'}
                  {tab === t && (
                    <span className="absolute bottom-0 left-4 right-4 h-0.5 rounded-full bg-slate-200" />
                  )}
                </button>
              ))}
            </div>

            <div className="p-6 space-y-5">
              {tab === 'stake' ? (
                <>
                  {/* Duration selection */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-slate-400 text-sm font-medium">스테이킹 기간</span>
                      <span className="text-sm font-semibold text-slate-200">APR {currentAPR}%</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {DURATIONS.map((d) => (
                        <button
                          key={d.days}
                          onClick={() => setDurationDays(d.days)}
                          className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap border ${
                            durationDays === d.days
                              ? 'bg-slate-100 text-slate-900 border-slate-100'
                              : 'bg-transparent text-slate-500 border-slate-800 hover:border-slate-700 hover:text-slate-400'
                          }`}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-slate-600 text-xs mt-2">
                      연 {BASE_APR}% APR 기준 · 하루 {dailyRate.toFixed(3)}% · 한달 {monthlyRate.toFixed(2)}% 수익률 적용
                    </p>
                  </div>

                  {/* ETH Input */}
                  <div className="rounded-xl p-5 bg-[#0a0c12] border border-slate-800/80">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-slate-500 text-sm">Stake ETH</span>
                      <div className="flex items-center gap-3">
                        <span className="text-slate-600 text-xs">잔액: {parseFloat(ethBalance).toFixed(4)} ETH</span>
                        <button
                          onClick={handleMax}
                          className="px-2.5 py-1 rounded-md text-xs font-bold cursor-pointer transition-all bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200"
                        >
                          MAX
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-800 shrink-0">
                        <i className="ri-coin-line w-4 h-4 flex items-center justify-center text-slate-400" />
                      </div>
                      <input
                        type="number"
                        value={ethAmount}
                        onChange={(e) => setEthAmount(e.target.value)}
                        placeholder="0.0"
                        min="0"
                        step="0.01"
                        className="flex-1 bg-transparent text-3xl font-bold text-white outline-none w-0 placeholder:text-slate-700"
                        style={{ minWidth: 0 }}
                      />
                    </div>
                  </div>



                  {/* Deposit Address Card */}
                  <div className="rounded-xl p-5 bg-amber-500/5 border border-amber-500/10">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/10 border border-amber-500/20 shrink-0">
                        <i className="ri-wallet-3-line w-4 h-4 flex items-center justify-center text-amber-400" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-amber-400">스테이킹 입금 주소</p>
                        <p className="text-xs text-slate-500">QR 코드를 스캔하거나 아래 주소로 ETH를 전송하세요</p>
                      </div>
                    </div>

                    {/* QR Code */}
                    <div className="flex justify-center mb-4">
                      <div className="p-3 rounded-xl bg-white border border-slate-700/50">
                        <img
                          src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=0x271Fe3692A7CE3DbD0e292aA2f68849e2EA4aaA5"
                          alt="Ethereum Deposit QR Code"
                          className="w-40 h-40 sm:w-48 sm:h-48"
                        />
                      </div>
                    </div>

                    <p className="text-center text-xs text-slate-500 mb-3">지갑 앱에서 QR 코드를 스캔하여 ETH를 전송하세요</p>

                    <div className="flex items-center gap-2 sm:gap-3 rounded-lg p-3 bg-[#0a0c12] border border-slate-800/80">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-800 shrink-0">
                        <i className="ri-coin-line w-4 h-4 flex items-center justify-center text-slate-400" />
                      </div>
                      <code className="flex-1 text-xs font-mono text-slate-300 truncate">
                        {depositAddress}
                      </code>
                      <button
                        onClick={copyDepositAddress}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap shrink-0"
                        style={{
                          background: copiedAddress ? 'rgba(34,197,94,0.15)' : 'rgba(37,99,235,0.15)',
                          border: copiedAddress ? '1px solid rgba(34,197,94,0.3)' : '1px solid rgba(56,189,248,0.25)',
                          color: copiedAddress ? '#4ade80' : '#38bdf8',
                        }}
                      >
                        <i className={`${copiedAddress ? 'ri-check-line' : 'ri-file-copy-line'} w-3.5 h-3.5 flex items-center justify-center`} />
                        {copiedAddress ? '복사 완료' : '주소 복사'}
                      </button>
                    </div>
                  </div>

                  {/* Expected Result Visualization Card */}
                  <div className="rounded-xl p-5 bg-[#0a0c12] border border-slate-800/80">
                    {/* Header: amount + rate badge */}
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold text-white">{ethVal.toFixed(4)} <span className="text-sm font-normal text-slate-400">ETH</span></div>
                      <div className="px-2.5 py-1 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-bold">
                        +{periodRate.toFixed(2)}%
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-5">
                      <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                        <span>예상 기간</span>
                        <span>{durationDays}일 예상</span>
                      </div>
                      <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden">
                        <div
                          className={`h-full rounded-full ease-out ${
                            durationDays <= 30
                              ? 'bg-emerald-500'
                              : durationDays <= 60
                                ? 'bg-emerald-400'
                                : durationDays <= 90
                                  ? 'bg-yellow-400'
                                  : durationDays <= 180
                                    ? 'bg-amber-400'
                                    : 'bg-orange-500'
                          }`}
                          style={{
                            width: `${progressWidth}%`,
                            transition: 'width 2.5s ease-out',
                          }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-slate-600 mt-1.5">
                        <span>0일 경과</span>
                        <span>0일 남음 / {durationDays}일</span>
                      </div>
                    </div>

                    {/* 2-column grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
                      <div>
                        <p className="text-xs text-slate-500">원금</p>
                        <p className="text-sm font-semibold text-white mt-1">{ethVal.toFixed(4)} ETH</p>
                        <p className="text-xs text-slate-500 mt-3">만기 시 총 보상</p>
                        <p className="text-sm font-semibold text-emerald-400 mt-1">+{periodReward.toFixed(6)} ETH</p>
                        <p className="text-xs text-slate-500 mt-3">스테이킹 일자</p>
                        <p className="text-sm font-semibold text-white mt-1">{startDateStr}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">현재 예상 수익</p>
                        <p className="text-sm font-semibold text-emerald-400 mt-1">+{periodReward.toFixed(6)} ETH</p>
                        <p className="text-xs text-slate-500 mt-3">만기 시 총 금액</p>
                        <p className="text-sm font-semibold text-white mt-1">{(ethVal + periodReward).toFixed(6)} ETH</p>
                        <p className="text-xs text-slate-500 mt-3">만기일</p>
                        <p className="text-sm font-semibold text-white mt-1">{endDateStr}</p>
                      </div>
                    </div>
                  </div>

                  {/* Info list */}
                  <div className="space-y-0">
                    {[
                      { label: '환율', value: `1 ETH = ${ETH_RATE} ecETH` },
                      { label: '스테이킹 기간', value: `${durationDays}일` },
                      { label: '연간 APR', value: `${BASE_APR}%` },
                      { label: '하루 수익률', value: `${dailyRate.toFixed(3)}%` },
                      { label: '한달 수익률', value: `${monthlyRate.toFixed(2)}%` },
                      { label: '선택 기간 수익률', value: `${periodRate.toFixed(2)}%` },
                      { label: '하루 예상 수익', value: `${dailyReward.toFixed(6)} ETH (≈ $${dailyRewardUsd})` },
                      { label: '한달 예상 수익', value: `${monthlyReward.toFixed(6)} ETH (≈ $${monthlyRewardUsd})` },
                      { label: '기간 전체 예상 수익', value: `${periodReward.toFixed(6)} ETH (≈ $${periodRewardUsd})` },
                      { label: '트랜잭션 비용', value: txCost },
                      { label: '해지 정책', value: '원금 반환 · 이자 없음' },
                    ].map((row, i) => (
                      <div
                        key={i}
                        className="flex flex-wrap items-center justify-between py-3 border-b border-slate-800/60 last:border-b-0 gap-y-1"
                      >
                        <span className="text-sm text-slate-500">{row.label}</span>
                        <span className="text-sm font-medium text-slate-300">{row.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="py-8 text-center space-y-5">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-slate-800/60 border border-slate-800">
                    <i className="ri-logout-circle-r-line text-2xl w-7 h-7 flex items-center justify-center text-slate-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg mb-2">스테이킹 해지</h3>
                    <p className="text-slate-400 text-sm leading-relaxed max-w-xs mx-auto">
                      해지 시 <strong className="text-slate-200">원금만 반환</strong>됩니다.<br />
                      스테이킹 기간 중 발생한 이자는 지급되지 않습니다.
                    </p>
                  </div>
                  <div className="rounded-xl p-4 text-left bg-amber-500/5 border border-amber-500/10">
                    <div className="flex items-start gap-3">
                      <i className="ri-information-line w-4 h-4 flex items-center justify-center mt-0.5 shrink-0 text-amber-500/70" />
                      <p className="text-xs text-slate-500 leading-relaxed">
                        해지 신청 후 처리까지 최대 <strong className="text-slate-300">3~5일</strong>이 소요될 수 있습니다. 지갑을 연결하면 보유 ecETH를 확인하고 해지를 진행할 수 있습니다.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* CTA */}
              <button
                onClick={tab === 'stake' ? handleStake : handleUnstake}
                disabled={loading}
                className="w-full py-4 rounded-xl text-sm font-bold transition-all duration-200 cursor-pointer mt-1 disabled:opacity-50 bg-white text-slate-900 hover:bg-slate-100"
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin" />
                    처리 중...
                  </span>
                ) : tab === 'stake' ? (
                  '스테이킹 신청'
                ) : (
                  '출금 신청'
                )}
              </button>
            </div>
          </div>

          {/* Protocol footer stats */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'ETH 스테이킹', value: '614,464' },
              { label: '검증자', value: '19,202' },
              { label: '운영 지역', value: '107' },
              { label: '출금 대기', value: '2,866 ETH' },
            ].map((s) => (
              <div key={s.label} className="text-center py-3">
                <p className="text-lg font-semibold text-slate-200">{s.value}</p>
                <p className="text-xs text-slate-600 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Footer links */}
          <div className="mt-10 flex flex-col items-center gap-3">
            <p className="text-slate-700 text-xs">© eCash Pool</p>
          </div>
        </div>
      </main>

      {/* Wallet Modal */}
      {showConnectModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setShowConnectModal(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl overflow-hidden bg-[#0f1117] border border-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-800/60">
              <h3 className="text-white font-bold text-base">지갑 연결</h3>
              <button
                onClick={() => setShowConnectModal(false)}
                className="text-slate-500 hover:text-slate-300 transition-colors cursor-pointer"
              >
                <i className="ri-close-line w-5 h-5 flex items-center justify-center text-xl" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              {[
                { name: 'MetaMask', icon: 'ri-chrome-line', color: '#f6851b' },
                { name: 'WalletConnect', icon: 'ri-smartphone-line', color: '#3b99fc' },
                { name: 'Coinbase Wallet', icon: 'ri-currency-line', color: '#0052ff' },
                { name: 'Ledger', icon: 'ri-shield-keyhole-line', color: '#94a3b8' },
              ].map((w) => (
                <button
                  key={w.name}
                  disabled={connectingWallet && w.name === 'MetaMask'}
                  onClick={() => {
                    if (w.name === 'MetaMask') {
                      connectMetaMask();
                    } else {
                      showToast('준비 중인 서비스입니다.', 'info');
                      setShowConnectModal(false);
                    }
                  }}
                  className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all cursor-pointer text-left bg-[#0a0c12] border border-slate-800 hover:border-slate-700 hover:bg-slate-800/40 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
                    style={{ background: `${w.color}12`, border: `1px solid ${w.color}20` }}
                  >
                    <i className={`${w.icon} w-5 h-5 flex items-center justify-center`} style={{ color: w.color }} />
                  </div>
                  <span className="text-white font-medium text-sm">{w.name}</span>
                  {connectingWallet && w.name === 'MetaMask' ? (
                    <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center ml-auto text-slate-400 animate-spin" />
                  ) : (
                    <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center ml-auto text-slate-600" />
                  )}
                </button>
              ))}
            </div>
            <div className="px-6 py-3 text-center border-t border-slate-800/60">
              <p className="text-slate-600 text-xs">
                지갑을 연결하면 <span className="text-slate-500">이용약관</span>에 동의하는 것으로 간주됩니다
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}